import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

export const GUESS_MAX = 100;

export interface GuessRecord {
	seat: PlayerIndex;
	value: number;
	/** `higher`, `lower`, or `got-it`. */
	hint: "higher" | "lower" | "got-it";
}

export interface NumberGuessState extends BaseState {
	kind: "numberguess";
	/** `0` once redacted — nobody sees it until somebody finds it. */
	secret: number;
	guesses: GuessRecord[];
	/** What is still possible, so the UI can narrow the buttons. */
	low: number;
	high: number;
}

export interface NumberGuessMove {
	guess: number;
}

export const numberGuess: GameDefinition<NumberGuessState, NumberGuessMove> = {
	id: "numberguess",
	category: "puzzle",
	title: "Number Guess Duel",
	emoji: "🔢",
	blurb: "A secret number between 1 and 100. Take turns — find it first.",
	howTo: [
		"There's a secret number between 1 and 100.",
		"Take turns guessing. You'll be told 'higher' or 'lower'.",
		"Both of you can see every guess, so listen carefully!",
		"First to say the number wins.",
	],

	create(rng) {
		return {
			kind: "numberguess",
			secret: 1 + rng.int(GUESS_MAX),
			guesses: [],
			low: 1,
			high: GUESS_MAX,
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const guess = (raw as { guess?: unknown }).guess;
		if (typeof guess !== "number" || !Number.isInteger(guess)) return null;
		if (guess < 1 || guess > GUESS_MAX) return null;
		return { guess };
	},

	apply(state, player, move): MoveOutcome<NumberGuessState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (state.guesses.some((entry) => entry.value === move.guess)) {
			return reject("Somebody has already tried that one.");
		}

		if (move.guess === state.secret) {
			const scores: [number, number] = [0, 0];
			scores[player] = 1;
			return {
				ok: true,
				state: {
					...state,
					guesses: [...state.guesses, { seat: player, value: move.guess, hint: "got-it" }],
					scores,
					turn: null,
					result: { winner: player },
				},
			};
		}

		const hint = move.guess < state.secret ? "higher" : "lower";
		return {
			ok: true,
			state: {
				...state,
				guesses: [...state.guesses, { seat: player, value: move.guess, hint }],
				low: hint === "higher" ? Math.max(state.low, move.guess + 1) : state.low,
				high: hint === "lower" ? Math.min(state.high, move.guess - 1) : state.high,
				turn: otherPlayer(player),
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const tried = new Set(state.guesses.map((entry) => entry.value));
		const moves: NumberGuessMove[] = [];
		// Only what's still possible: the hints have ruled the rest out.
		for (let guess = state.low; guess <= state.high; guess++) {
			if (!tried.has(guess)) moves.push({ guess });
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state) {
		// Sending the secret would end the game rather quickly.
		return state.result ? state : { ...state, secret: 0 };
	},
};
